/**
 * <s-auto-carousel> slides a horizontal track of cards forward one card at a
 * time on a timer, looping back to the start. Pauses while hovered, focused,
 * off-screen or the tab is hidden, and never autoplays for reduced motion.
 */
class AutoCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;
    this.interval = Number(this.dataset.interval) || 4000;
    this.hovered = false;
    this.focused = false;
    this.visible = false;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.step(1));
    this.addEventListener('pointerenter', () => { this.hovered = true; });
    this.addEventListener('pointerleave', () => { this.hovered = false; });
    this.addEventListener('focusin', () => { this.focused = true; });
    this.addEventListener('focusout', () => { this.focused = false; });
    this.track.addEventListener('touchstart', () => { this.hovered = true; }, { passive: true });
    this.track.addEventListener('touchend', () => { setTimeout(() => { this.hovered = false; }, 3000); }, { passive: true });

    this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; }, { threshold: 0.3 });
    this.observer.observe(this);

    if (!this.reduced) this.timer = setInterval(() => this.tick(), this.interval);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    this.observer?.disconnect();
  }

  tick() {
    if (this.hovered || this.focused || !this.visible || document.hidden) return;
    this.step(1);
  }

  step(direction) {
    const track = this.track;
    const card = track.firstElementChild;
    if (!card) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = card.getBoundingClientRect().width + gap;
    const rtl = getComputedStyle(track).direction === 'rtl';
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    const behavior = this.reduced ? 'auto' : 'smooth';
    let target;
    if (direction > 0) target = position >= max - 4 ? 0 : Math.min(position + distance, max);
    else target = position <= 4 ? max : Math.max(position - distance, 0);
    track.scrollTo({ left: rtl ? -target : target, behavior });
  }
}

if (!customElements.get('s-auto-carousel')) {
  customElements.define('s-auto-carousel', AutoCarousel);
}
